import { FormEvent, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { CreatePolicyComponentGeneralStepContainer } from "@containers/common/rp-service-configuration-policy-decision-container/policy-decision-create-policy-component-container/general-step/create-policy-component-general-step-container";
import { CreatePolicyComponentTriggerConfigurationContainer } from "@containers/common/rp-service-configuration-policy-decision-container/policy-decision-create-policy-component-container/trigger-configuration/create-policy-component-trigger-configuration-container";
import { PolicyComponentCreationModeTypes } from "@enums/policy-component-creation-mode-types";
import { PolicyComponentCreationStepTypes } from "@enums/policy-component-creation-step-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { PolicyComponentTypes } from "@enums/policy-component-types";
import { ButtonTypes } from "@iddataweb/idw-common-ui/enums";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { AvailableAssertionStatus, DeployedPolicyComponent, SimpleDeployedPolicyComponentCondition } from "@idw-types/data/reference-data/generic";
import { FormattedPolicyComponent, PolicyComponent } from "@idw-types/data/reference-data/generic/policy-component";
import { FormattedRpServiceConfigurationServiceOffering, FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { createPolicyComponentReduxRequest } from "@redux-requests/policy-component/create-policy-component";
import { getCreatePolicyComponentValidationErrors } from "@utils/validation/create-policy-component/create-policy-component-validation-util";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";

type AddPolicyComponentModalProps = {
    axnCompanies: Array<AxnCompany>;
    allPolicyComponents: Array<DeployedPolicyComponent>;
    policyComponents: Array<DeployedPolicyComponent>;
    policyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    acquiredAttributes: Array<AxnAcquiredAttribute>;
    availableAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    allAxnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypes: Array<AxnAttributeType>;
    allAxnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAcquiredAttributeTypeFields: Array<AxnAttributeTypeField>;
    updateCommonReferenceData: boolean;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    onModalClose: () => void;
    setPolicyComponent: (policyComponent: PolicyComponent) => void;
}

export const AddPolicyComponentModal = (props: AddPolicyComponentModalProps) => {
    const {
        axnCompanies,
        allPolicyComponents,
        policyComponents,
        policyComponentConditions,
        assertions,
        availableAssertions,
        serviceOfferings,
        acquiredAttributes,
        allAxnAttributeTypes,
        axnAttributeTypes,
        allAxnAttributeTypeFields,
        axnAttributeTypeFields,
        axnAcquiredAttributeTypeFields,
        updateCommonReferenceData,
        axnAvailableAssertionStatuses,
        onModalClose,
        setPolicyComponent
    } = props;

    const [currentStep, setCurrentStep] = useState(PolicyComponentCreationStepTypes.COMPONENT_DETAILS);
    const [policyComponentState, setPolicyComponentState] = useState<FormattedPolicyComponent>(
        {
            conditions: [],
            name: "",
            type: PolicyComponentTypes.AND,
            defaultValue: "",
            status: EntityStatusTypes.ACTIVE,
            category: null,
            organizationID: axnCompanies.length === 1 ? axnCompanies[0].id : null,
            description: "",
            id: null
        }
    );
    const [policyComponentCreationMode, setPolicyComponentCreationMode] = useState(PolicyComponentCreationModeTypes.FROM_SCRATCH);
    const [selectedPolicyComponent, setSelectedPolicyComponent] = useState(null);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const dispatch = useDispatch();
    const createPolicyComponentResource = resources.modals.createPolicyComponent;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const updatedPolicyComponent = await createPolicyComponentReduxRequest(dispatch)(policyComponentState, updateCommonReferenceData);

        setRequestInProgress(false);

        if (updatedPolicyComponent) {
            setPolicyComponent(updatedPolicyComponent);
            onModalClose();
        }
    }

    const handleNextStep = () => {
        setCurrentStep(PolicyComponentCreationStepTypes.TRIGGER_CONFIGURATION);
    }

    const handlePreviousStep = () => {
        setCurrentStep(PolicyComponentCreationStepTypes.COMPONENT_DETAILS);
    }

    const isPolicyComponentNameAlreadyUsed = useMemo(() => {
        return policyComponents.some(policyComponent => policyComponent.name === policyComponentState.name);
    }, [policyComponents, policyComponentState.name]);

    const validationErrors = useMemo(() => {
        return getCreatePolicyComponentValidationErrors(policyComponentState, isPolicyComponentNameAlreadyUsed);
    }, [policyComponentState, isPolicyComponentNameAlreadyUsed]);

    return (
        <DefaultModal
            automationId="create-policy-component-modal"
            ariaLabelledBy="create-policy-component-modal"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"create-policy-component-header"} closeButton>
                    <Modal.Title>{createPolicyComponentResource.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentStep === PolicyComponentCreationStepTypes.COMPONENT_DETAILS && (
                        <CreatePolicyComponentGeneralStepContainer
                            policyComponents={policyComponents}
                            policyComponentConditions={policyComponentConditions}
                            companies={axnCompanies}
                            policyComponentState={policyComponentState}
                            validationErrors={validationErrors.generalStep}
                            policyComponentCreationMode={policyComponentCreationMode}
                            selectedPolicyComponent={selectedPolicyComponent}
                            setPolicyComponentCreationMode={setPolicyComponentCreationMode}
                            setSelectedPolicyComponent={setSelectedPolicyComponent}
                            setPolicyComponentState={setPolicyComponentState}
                        />
                    )}
                    {currentStep === PolicyComponentCreationStepTypes.TRIGGER_CONFIGURATION && (
                        <CreatePolicyComponentTriggerConfigurationContainer
                            policyComponentState={policyComponentState}
                            assertions={assertions}
                            allPolicyComponents={allPolicyComponents}
                            availableAssertions={availableAssertions}
                            serviceOfferings={serviceOfferings}
                            allAxnAttributeTypes={allAxnAttributeTypes}
                            axnAttributeTypes={axnAttributeTypes}
                            allAxnAttributeTypeFields={allAxnAttributeTypeFields}
                            axnAttributeTypeFields={axnAttributeTypeFields}
                            axnAcquiredAttributeTypeFields={axnAcquiredAttributeTypeFields}
                            policyComponents={policyComponents}
                            acquiredAttributes={acquiredAttributes}
                            axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                            validationErrors={validationErrors.triggerConfiguration}
                            setPolicyComponentState={setPolicyComponentState}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        id="create-policy-component-modal-cancel-button"
                        variant="secondary"
                        onClick={onModalClose}>
                        {resources.common.buttons.cancel.label}
                    </Button>
                    {currentStep === PolicyComponentCreationStepTypes.TRIGGER_CONFIGURATION && (
                        <Button
                            id="create-policy-component-modal-back-button"
                            variant="primary"
                            onClick={handlePreviousStep}>
                            {"Back"}
                        </Button>
                    )}
                    {currentStep === PolicyComponentCreationStepTypes.COMPONENT_DETAILS && (
                        <Button
                            id="create-policy-component-modal-next-button"
                            disabled={validationErrors.generalStep.count > 0}
                            variant="primary"
                            onClick={handleNextStep}>
                            {"Next"}
                        </Button>
                    )}
                    {currentStep === PolicyComponentCreationStepTypes.TRIGGER_CONFIGURATION &&
                        <ButtonWithSpinner
                            hideSpinner={!requestInProgress || validationErrors.count > 0}
                            id="create-policy-component-modal-save-button"
                            disabled={requestInProgress || validationErrors.count > 0}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.save.label}
                        ></ButtonWithSpinner>
                    }

                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
}